import React, { useEffect, useState, useRef } from 'react';
import './DealNotes.css';

const API_BASE = window.location.port === '3000'
  ? 'http://localhost:4100/api'
  : window.location.pathname.startsWith('/cmbs')
    ? '/cmbs/api'
    : '/api';

const SECTIONS = [
  { key: 'property_overview',     label: 'Property Overview',     default_mode: 'scannable' },
  { key: 'tenants',               label: 'Tenants',               default_mode: 'scannable' },
  { key: 'sponsorship',           label: 'Sponsorship',           default_mode: 'scannable' },
  { key: 'loan_request',          label: 'Loan Request',          default_mode: 'scannable' },
  { key: 'future_capex',          label: 'Future CapEx',          default_mode: 'scannable' },
  { key: 'investment_highlights', label: 'Investment Highlights', default_mode: 'detailed' },
  { key: 'risk_considerations',   label: 'Risk Considerations',   default_mode: 'detailed' },
  { key: 'questions',             label: 'Questions',             default_mode: 'scannable' }
];

function DealNotes({ deal, token }) {
  const [notes, setNotes] = useState(null);
  const [prerequisites, setPrerequisites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [savingNow, setSavingNow] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const saveTimerRef = useRef(null);

  useEffect(() => { loadNotes(); }, [deal.id]);

  async function apiFetch(path, opts = {}) {
    const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token}` };
    if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
      opts = { ...opts, body: JSON.stringify(opts.body) };
    }
    return fetch(`${API_BASE}${path}`, { ...opts, headers });
  }

  async function loadNotes() {
    setLoading(true);
    try {
      const res = await apiFetch(`/deal-notes/${deal.id}`);
      const data = await res.json();
      setNotes(data.notes);
      setPrerequisites(data.prerequisites);
      if (data.notes?.last_saved_at) setLastSaved(new Date(data.notes.last_saved_at));
    } catch (e) {
      console.error('loadNotes error', e);
    } finally {
      setLoading(false);
    }
  }

  async function draftNow() {
    setLoading(true);
    try {
      const res = await apiFetch(`/deal-notes/${deal.id}/draft-now`, { method: 'POST' });
      const data = await res.json();
      setNotes(data.notes);
      if (data.notes?.last_saved_at) setLastSaved(new Date(data.notes.last_saved_at));
    } catch (e) {
      alert('Draft failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  function scheduleAutosave(updatedSections) {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSavingNow(true);
      try {
        const res = await apiFetch(`/deal-notes/${deal.id}`, {
          method: 'PUT',
          body: { sections: updatedSections }
        });
        const data = await res.json();
        if (data.notes?.last_saved_at) setLastSaved(new Date(data.notes.last_saved_at));
      } finally {
        setSavingNow(false);
      }
    }, 1000);
  }

  function updateBulletText(sectionKey, bulletId, newText, group) {
    const updated = { ...notes.sections };
    const section = updated[sectionKey] || {};

    if (sectionKey === 'questions' && group) {
      const bullets = [...(section[group] || [])];
      const idx = bullets.findIndex(b => b.id === bulletId);
      if (idx === -1) return;
      bullets[idx] = { ...bullets[idx], text: newText };
      updated[sectionKey] = { ...section, [group]: bullets };
    } else {
      const bullets = [...(section.bullets || [])];
      const idx = bullets.findIndex(b => b.id === bulletId);
      if (idx === -1) return;
      bullets[idx] = { ...bullets[idx], text: newText };
      updated[sectionKey] = { ...section, bullets };
    }

    setNotes({ ...notes, sections: updated });
    scheduleAutosave(updated);
  }

  function deleteBullet(sectionKey, bulletId, group) {
    const updated = { ...notes.sections };
    const section = updated[sectionKey] || {};

    if (sectionKey === 'questions' && group) {
      updated[sectionKey] = { ...section, [group]: (section[group] || []).filter(b => b.id !== bulletId) };
    } else {
      updated[sectionKey] = { ...section, bullets: (section.bullets || []).filter(b => b.id !== bulletId) };
    }

    setNotes({ ...notes, sections: updated });
    scheduleAutosave(updated);
  }

  function addBullet(sectionKey, group) {
    const updated = { ...notes.sections };
    const section = updated[sectionKey] || {};
    const newBullet = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Date.now().toString(36),
      text: '',
      source_citation: 'Analyst note'
    };

    if (sectionKey === 'questions' && group) {
      updated[sectionKey] = { ...section, [group]: [...(section[group] || []), newBullet] };
    } else {
      updated[sectionKey] = { ...section, bullets: [...(section.bullets || []), newBullet] };
    }

    setNotes({ ...notes, sections: updated });
    scheduleAutosave(updated);
  }

  function reorderBullet(sectionKey, bulletId, direction, group) {
    const updated = { ...notes.sections };
    const section = updated[sectionKey] || {};
    const list = (sectionKey === 'questions' && group) ? (section[group] || []) : (section.bullets || []);
    const bullets = [...list];
    const idx = bullets.findIndex(b => b.id === bulletId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= bullets.length) return;
    [bullets[idx], bullets[swapIdx]] = [bullets[swapIdx], bullets[idx]];

    if (sectionKey === 'questions' && group) {
      updated[sectionKey] = { ...section, [group]: bullets };
    } else {
      updated[sectionKey] = { ...section, bullets };
    }

    setNotes({ ...notes, sections: updated });
    scheduleAutosave(updated);
  }

  async function regenerateSection(sectionKey, sectionLabel, mode) {
    const ok = window.confirm(
      `Replace/reword this section entirely?\n\nSection: ${sectionLabel}\nMode: ${mode}\n\nOK = replace all current bullets with a fresh Claude draft. Other sections are untouched.`
    );
    if (!ok) return;

    setRegenerating(sectionKey);
    try {
      const res = await apiFetch(`/deal-notes/${deal.id}/regenerate-section`, {
        method: 'POST',
        body: { section_key: sectionKey, display_mode: mode }
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const updated = { ...notes.sections, [sectionKey]: data.result };
      setNotes({ ...notes, sections: updated });
      scheduleAutosave(updated);
    } catch (err) {
      alert(`Regeneration failed: ${err.message}`);
    } finally {
      setRegenerating(null);
    }
  }

  function getDisplayMode(sectionKey) {
    return notes?.sections?.[sectionKey]?.display_mode
      || SECTIONS.find(s => s.key === sectionKey)?.default_mode
      || 'scannable';
  }

  function formatLastSaved() {
    if (savingNow) return 'Saving...';
    if (!lastSaved) return null;
    const secs = Math.floor((Date.now() - lastSaved.getTime()) / 1000);
    if (secs < 5) return 'Saved just now';
    if (secs < 60) return `Saved ${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Saved ${mins}m ago`;
    return `Saved at ${lastSaved.toLocaleTimeString()}`;
  }

  const [, forceTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceTick(t => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="deal-notes-loading">Loading deal notes...</div>;

  if (!notes) {
    return (
      <div className="deal-notes-empty">
        <h2>Deal Notes</h2>
        <p>Claude will draft initial notes as soon as one of the following is provided:</p>
        <ul className="prereq-list">
          <li className={prerequisites?.broker_email ? 'prereq-met' : 'prereq-pending'}>
            {prerequisites?.broker_email ? '✓' : '○'} Broker email (paste or upload at Deal Intake)
          </li>
          <li className={prerequisites?.offering_memorandum ? 'prereq-met' : 'prereq-pending'}>
            {prerequisites?.offering_memorandum ? '✓' : '○'} Offering Memorandum uploaded
          </li>
          <li className={prerequisites?.extraction_complete ? 'prereq-met' : 'prereq-pending'}>
            {prerequisites?.extraction_complete ? '✓' : '○'} Extraction completed with approved fields
          </li>
        </ul>
        <button className="btn-primary btn-large" onClick={draftNow}>Draft Notes Now</button>
      </div>
    );
  }

  return (
    <div className="deal-notes-page">
      <div className="deal-notes-header">
        <div>
          <h2>Deal Notes</h2>
          <div className="deal-notes-subtitle">{deal.property_name}</div>
        </div>
        <div className="deal-notes-meta">
          <div className="save-indicator">{formatLastSaved()}</div>
        </div>
      </div>

      {SECTIONS.map(section => {
        const content = notes.sections?.[section.key] || {};
        const currentMode = getDisplayMode(section.key);
        const isQuestions = section.key === 'questions';

        return (
          <div key={section.key} className="notes-section">
            <div className="notes-section-header">
              <h3>{section.label}</h3>
              <div className="section-controls">
                <div className="mode-toggle">
                  <button
                    className={`mode-btn ${currentMode === 'scannable' ? 'active' : ''}`}
                    onClick={() => regenerateSection(section.key, section.label, 'scannable')}
                    disabled={regenerating !== null}
                    title="Short scannable bullets"
                  >
                    Scannable
                  </button>
                  <button
                    className={`mode-btn ${currentMode === 'detailed' ? 'active' : ''}`}
                    onClick={() => regenerateSection(section.key, section.label, 'detailed')}
                    disabled={regenerating !== null}
                    title="Longer prose-style bullets"
                  >
                    Detailed
                  </button>
                </div>
                <button
                  className="btn-chip"
                  onClick={() => regenerateSection(section.key, section.label, currentMode)}
                  disabled={regenerating !== null}
                >
                  {regenerating === section.key ? 'Regenerating...' : 'Regenerate'}
                </button>
              </div>
            </div>

            {content.error && (
              <div className="notes-section-error">Error: {content.error}</div>
            )}

            {isQuestions ? (
              <QuestionsSection
                content={content}
                sectionKey={section.key}
                onUpdate={updateBulletText}
                onDelete={deleteBullet}
                onAdd={addBullet}
                onReorder={reorderBullet}
              />
            ) : (
              <RegularBulletList
                bullets={content.bullets || []}
                sectionKey={section.key}
                onUpdate={updateBulletText}
                onDelete={deleteBullet}
                onAdd={addBullet}
                onReorder={reorderBullet}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function QuestionsSection({ content, sectionKey, onUpdate, onDelete, onAdd, onReorder }) {
  const standardBullets = content.standard_bullets || [];
  const dealSpecificBullets = content.deal_specific_bullets || [];

  return (
    <>
      <div className="questions-group">
        <div className="questions-group-label">Standard Questions</div>
        <ol className="notes-bullets numbered">
          {standardBullets.map((b, idx) => (
            <BulletItem
              key={b.id}
              bullet={b}
              idx={idx}
              total={standardBullets.length}
              sectionKey={sectionKey}
              group="standard_bullets"
              onUpdate={onUpdate}
              onDelete={onDelete}
              onReorder={onReorder}
            />
          ))}
        </ol>
        <button className="btn-add-bullet" onClick={() => onAdd(sectionKey, 'standard_bullets')}>
          + Add standard question
        </button>
      </div>

      <div className="questions-group">
        <div className="questions-group-label">Deal-Specific Questions</div>
        {dealSpecificBullets.length === 0 ? (
          <p className="empty">(No deal-specific questions generated)</p>
        ) : (
          <ol className="notes-bullets numbered">
            {dealSpecificBullets.map((b, idx) => (
              <BulletItem
                key={b.id}
                bullet={b}
                idx={idx}
                total={dealSpecificBullets.length}
                sectionKey={sectionKey}
                group="deal_specific_bullets"
                onUpdate={onUpdate}
                onDelete={onDelete}
                onReorder={onReorder}
              />
            ))}
          </ol>
        )}
        <button className="btn-add-bullet" onClick={() => onAdd(sectionKey, 'deal_specific_bullets')}>
          + Add deal-specific question
        </button>
      </div>
    </>
  );
}

function RegularBulletList({ bullets, sectionKey, onUpdate, onDelete, onAdd, onReorder }) {
  return (
    <>
      <ul className="notes-bullets">
        {bullets.map((b, idx) => (
          <BulletItem
            key={b.id}
            bullet={b}
            idx={idx}
            total={bullets.length}
            sectionKey={sectionKey}
            group={null}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onReorder={onReorder}
          />
        ))}
      </ul>
      <button className="btn-add-bullet" onClick={() => onAdd(sectionKey, null)}>
        + Add bullet
      </button>
    </>
  );
}

function BulletItem({ bullet: b, idx, total, sectionKey, group, onUpdate, onDelete, onReorder }) {
  return (
    <li className="notes-bullet">
      {b.level && <span className={`badge badge-${b.level}`}>{b.level}</span>}
      <textarea
        className="bullet-text"
        value={b.text || ''}
        onChange={(e) => onUpdate(sectionKey, b.id, e.target.value, group)}
        rows={Math.max(1, Math.ceil((b.text || '').length / 90))}
      />
      <div className="bullet-actions">
        {b.source_citation && b.source_citation !== 'n/a' && (
          <span className={`cite${b.source_citation === 'Analyst note' ? ' cite-analyst' : ''}`}>
            {b.source_citation}
          </span>
        )}
        <button
          className="bullet-move"
          onClick={() => onReorder(sectionKey, b.id, 'up', group)}
          disabled={idx === 0}
        >↑</button>
        <button
          className="bullet-move"
          onClick={() => onReorder(sectionKey, b.id, 'down', group)}
          disabled={idx === total - 1}
        >↓</button>
        <button
          className="bullet-delete"
          onClick={() => onDelete(sectionKey, b.id, group)}
        >×</button>
      </div>
    </li>
  );
}

export default DealNotes;
